import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import parse from 'style-to-object';
import './draggable-entity-card.css';
import {
	ENTITY_FA_ICON_NAMES,
	getCanvasEntityDOMId,
	resolveEntityColorClass,
	getCardGradientStatement,
	fromDisabledReasonsToCardState
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import TestEntityButton from './TestEntityButton';
import {
	ChangeSet,
	EntityType,
	EventEntityState,
	lowercaseFirstLetter,
	ProjectState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	addDisabledReasons,
	removeDisabledReasons,
	READABLE_ENTITY_TYPES,
	StateMutationAction,
  safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import ArgumentDeclaration from './ArgumentDeclaration';
import {
	getEntityArgumentDeclarations,
	getRelatedCanvasElementsOnTheRight
} from '@elyx-code/project-logic-tree';
import CollapseEntityButton from './CollapseEntityButton';
import LogicScopeCallerNode from './LogicScopeCallerNode';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import { useNodeDescriptionPopup } from './useNodeDescriptionPopup';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';
import { EditorService } from '../../services/editor';
import EntityLockedIconMenu from './entity-locked-icon/EntityLockedIcon';
import { Logger } from '@elyx-code/common-ts-utils';
import { popupNotification } from '../../popup-notification';

interface IEventCardProps extends ICanvasElementComponentProps {
	entity: EventEntityState;
}
const EventCard = (props: IEventCardProps) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useNodeDescriptionPopup({
		entity: props.entity,
		canvasElement: props.canvasElement,
		project: props.project
	});

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: 'canvas-event-card-menu-button--' + props.entity.id + '--popup',
			anchorQuerySelector:
				'#canvas-event-card-menu-button--' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject
		},
		(popup) => (
			<CanvasPopupBaseComponent>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								await props.project.remove(props.entity.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: entity.deletable
								? 'Forever delete this node'
								: 'This node cannot be deleted. Because the system need to refer to it over time.',
							parentCanvasObject: popup.self,
							id:
								'canvas-event-card--' +
								props.entity.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								Logger.log(
									'Navigating to: ',
									`${props.project?.editorBasePath}/entity/${EntityType.GlobalEvent}/${props.entity.id}`
								);
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.GlobalEvent}/${props.entity.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							tooltip: !props.entity.editable
								? 'This node has been locked and can no longer be edited'
								: 'Edit this node',
							parentCanvasObject: popup.self,
							id:
								'canvas-event-card--' +
								props.entity.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.clearEntityTestValues(props.entity);
							},
							label: 'Clear test values',
							iconEnd: 'fa-solid fa-eraser',
							disabled: !props.project.hasTestValuesForEntity(props.entity),
							tooltip: props.project.hasTestValuesForEntity(props.entity)
								? 'Clear all test values (purple overrides) for this node'
								: 'No test values to clear for this node',
							parentCanvasObject: popup.self,
							id:
								'canvas-event-card--' +
								props.entity.id +
								'--clear-test-values'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											StateMutationAction.RecalculateGlobalEvent
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		)
	);

	useEffect(() => {
		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityOtherDisabledReason[] = [];

		// @ts-ignore
		if (props.entity.suggestion) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.Suggestion
			);
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			// This handles the case where canvas was disabled but is now enabled
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleEntityUpdate = (payload: any) => {
			let hasUpdated = false;
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			const disabledReasonsToRemove: CanvasEntityTestingDisabledReason[] =
				[];

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				// @ts-ignore
				props.entity
			);

			if (
				!!props.project.test &&
				// @ts-ignore
				!props.project.test?.hasEntity(props.entity) &&
				// @ts-ignore
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true; // Mark as updated
			}

			if (
				// @ts-ignore
				(props.project.test?.hasEntity(props.entity) ||
					// @ts-ignore
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true;
			}

			if (
				!!props.project.test &&
				// @ts-ignore
				!props.project.test?.hasEntity(props.entity)
			) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
				hasUpdated = true; // Mark as updated
			}

			if (newDisabledReasons.length || disabledReasonsToRemove.length) {
				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newDisabledReasons
					);
					const finalConfig = removeDisabledReasons(
						config.reasons,
						disabledReasonsToRemove
					);
					return finalConfig.reasons;
				});
				// We already set hasUpdated, so no need to set it again
			}

			if (hasUpdated) {
				forceUpdate(); // This is fine if hasUpdated is true
			} else {
				forceUpdate(); // This was in your original logic
			}
		};

		const handleTestSetupStarted = () => {
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];

			if (
				!!props.project.test &&
				// @ts-ignore
				!props.project.test?.hasEntity(props.entity) &&
				// @ts-ignore
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					props.entity
				);

				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			// @ts-ignore
			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			// @ts-ignore
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');

					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		// @ts-ignore
		props.entity.suggestion
	]);

	useEffect(() => {
		props.canvasElement.draggableObject.updateConnections();
	});

	const entity = props.entity;

	const children = getRelatedCanvasElementsOnTheRight(props.entity);

	const argumentDeclarations = getEntityArgumentDeclarations(entity);

	const classes = ['canvas-draggable-card'];

	return (
		<div
			ref={rootElementRef}
			className={classes.join(' ')}
			id={getCanvasEntityDOMId(props.entity)}
			onClick={(e) => {
				e.stopPropagation();
			}}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
		>
			<TestEntityButton
				entity={props.entity}
				canvasElement={props.canvasElement}
				project={props.project}
				parentDisabledReasons={disabledReasons}
			/>
			{children.length > 0 && (
				<CollapseEntityButton
					entity={props.entity}
					project={props.project}
					canvasElement={props.canvasElement}
					parentDisabledReasons={disabledReasons}
				/>
			)}
			<div
				className={[
					'canvas-draggable-card__body',
					...(!!disabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: []),
					resolveEntityColorClass(props.entity.type, {
						state: fromDisabledReasonsToCardState(disabledReasons)
					})
				].join(' ')}
			>
				<header
					className="canvas-draggable-card__header"
					{...{
						[Canvas.OBJ_ATTR_NAME]:
							getCanvasEntityDOMId(props.entity) + '--header'
					}}
					style={parse(
						getCardGradientStatement(entity, {
							state: fromDisabledReasonsToCardState(
								disabledReasons
							)
						})
					)}
				>
					<div className="canvas-draggable-card__header__left-block">
						<i
							className={[
								'canvas-draggable-card-header__icon',
								'fa-solid',
								'fa-' +
									(ENTITY_FA_ICON_NAMES[entity.type] ||
										'rectangle-list')
							].join(' ')}
						/>
						<span className="canvas-draggable-card__header__labels">
							<h3>
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</h3>
							<p>
								{READABLE_ENTITY_TYPES[entity.type]?.singular}
							</p>
						</span>
					</div>
					<div className="canvas-draggable-card__header__actions">
						<EntityLockedIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<LogicErrorIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<Button
							ref={canvasPopup.anchorRef}
							variant="text"
							color="grey"
							iconEnd="fa-solid fa-ellipsis-v"
							size="small"
							id={'canvas-event-card-menu-button--' + entity.id}
							disabled={!!disabledReasons.length}
							onClick={(e: any) => {
								e.stopPropagation();
								e.preventDefault();
								canvasPopup?.toggle();
							}}
							onDoubleClick={(
								e: React.MouseEvent<HTMLButtonElement>
							) => {
								e.stopPropagation();
								e.preventDefault();
								if (disabledReasons.length) return;
								props.project?.canvas?.unselectAll();
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.GlobalEvent}/${props.entity.id}`
								);
							}}
						/>
					</div>
					<LogicScopeCallerNode
						entity={props.entity}
						draggableObject={props.canvasElement.draggableObject}
						project={props.project}
						parentDisabledReasons={disabledReasons}
					/>
				</header>
				<div
					className={[
						'canvas-draggable-card__content',
						'canvas-draggable-card__content__small-padding-bottom'
					].join(' ')}
				>
					{argumentDeclarations.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{argumentDeclarations
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((argumentDeclaration) => (
									<ArgumentDeclaration
										key={argumentDeclaration.id}
										argumentDeclaration={
											argumentDeclaration
										}
										entity={props.entity}
										canvasElement={props.canvasElement}
										project={props.project}
										parentDisabledReasons={disabledReasons}
									/>
								))}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
};

export default EventCard;
